/**
 * Minimal Ghost Content API client (read-only, public key).
 * https://docs.ghost.org/content-api/
 *
 * Plain fetch, no SDK. Works in the browser and in the Node prerender.
 * Posts are always requested with `include=tags,authors` (the docs warn that
 * `fields` does not play well with `include`, so it is not used).
 */
import { DEFAULT_LOCALE, type Locale } from "@thyra/landing-i18n";
import { sanitizePostHtml } from "./sanitize";
import {
  TAGGED_LOCALES,
  isTranslationKey,
  langFromTagSlugs,
  langTagSlug,
  translationKeyFromTagSlugs,
  translationTagSlug,
  type TranslationLink,
} from "./blog-lang";

declare global {
  interface Window {
    __GHOST_URL__?: string;
    __GHOST_CONTENT_KEY__?: string;
  }
}

export type GhostConfig = { url: string; key: string };

export type BlogTag = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  /** Public posts carrying the tag (`include=count.posts`), when known. */
  postCount: number | null;
};

export type BlogAuthor = {
  id: string;
  slug: string;
  name: string;
  profileImage: string | null;
  bio: string | null;
};

export type BlogPostSummary = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featureImage: string | null;
  featureImageAlt: string | null;
  featureImageCaption: string | null;
  publishedAt: string;
  updatedAt: string;
  readingTime: number;
  tags: BlogTag[];
  primaryTag: BlogTag | null;
  /** Language of the content (`#lang-*` internal tag; English when absent). */
  lang: Locale;
  /** `#t-<key>` internal tag: links translations of the same article. */
  translationKey: string | null;
  author: BlogAuthor | null;
  metaTitle: string | null;
  metaDescription: string | null;
  ogImage: string | null;
};

export type BlogPost = BlogPostSummary & {
  /** Sanitized HTML, safe for dangerouslySetInnerHTML. */
  html: string;
};

export type BlogPagination = {
  page: number;
  limit: number;
  pages: number;
  total: number;
  next: number | null;
  prev: number | null;
};

export type BlogPage = { posts: BlogPostSummary[]; pagination: BlogPagination };

/* ------------------------------------------------------------------ */
/* Config                                                              */
/* ------------------------------------------------------------------ */

/** Pulumi / raw K8s manifests use `__TOKEN__` placeholders until rendered. */
function usable(value: string | undefined): string {
  const v = (value ?? "").trim();
  return /^__[A-Z_][A-Z0-9_]*__$/.test(v) ? "" : v;
}

/**
 * Browser: window.__GHOST_URL__ / __GHOST_CONTENT_KEY__ (config.js), then the
 * build-time VITE_* values. Build/SSR (Node): process.env. `null` when the
 * blog is not configured.
 */
export function resolveGhostConfig(): GhostConfig | null {
  const env = import.meta.env;
  const proc = typeof process !== "undefined" ? process.env : undefined;
  const candidates =
    typeof window !== "undefined"
      ? {
          url: [window.__GHOST_URL__, env.VITE_GHOST_URL],
          key: [window.__GHOST_CONTENT_KEY__, env.VITE_GHOST_CONTENT_API_KEY],
        }
      : {
          url: [proc?.VITE_GHOST_URL, env.VITE_GHOST_URL],
          key: [
            proc?.GHOST_CONTENT_API_KEY,
            proc?.VITE_GHOST_CONTENT_API_KEY,
            env.VITE_GHOST_CONTENT_API_KEY,
          ],
        };
  const url = candidates.url.map(usable).find(Boolean) ?? "";
  const key = candidates.key.map(usable).find(Boolean) ?? "";
  if (!url || !key || !/^https?:\/\//i.test(url)) return null;
  return { url: url.replace(/\/+$/, ""), key };
}

/** Host of the configured Ghost, used to turn CMS links into blog links. */
function ghostHost(): string | null {
  const url = resolveGhostConfig()?.url;
  try {
    return url ? new URL(url).host : null;
  } catch {
    return null;
  }
}

/** Origin of the configured Ghost (for <link rel="preconnect">), or null. */
export function ghostOrigin(): string | null {
  const url = resolveGhostConfig()?.url;
  try {
    return url ? new URL(url).origin : null;
  } catch {
    return null;
  }
}

export class GhostError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "GhostError";
  }
}

/* ------------------------------------------------------------------ */
/* Raw API shapes (fields as documented for the Content API)           */
/* ------------------------------------------------------------------ */

type RawTag = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  visibility?: string;
  count?: { posts?: number };
};
type RawAuthor = {
  id: string;
  slug: string;
  name: string;
  profile_image?: string | null;
  bio?: string | null;
};
export type RawPost = {
  id: string;
  slug: string;
  title: string;
  html?: string | null;
  excerpt?: string | null;
  custom_excerpt?: string | null;
  feature_image?: string | null;
  feature_image_alt?: string | null;
  feature_image_caption?: string | null;
  published_at: string;
  updated_at: string;
  reading_time?: number | null;
  tags?: RawTag[];
  primary_tag?: RawTag | null;
  authors?: RawAuthor[];
  primary_author?: RawAuthor | null;
  meta_title?: string | null;
  meta_description?: string | null;
  og_image?: string | null;
};

/* ------------------------------------------------------------------ */
/* Mapping                                                             */
/* ------------------------------------------------------------------ */

const WORDS_PER_MINUTE = 265;

/** Ghost's own `reading_time` when present, else an estimate from the HTML. */
export function readingTimeMinutes(
  ghostValue: number | null | undefined,
  html: string,
): number {
  if (typeof ghostValue === "number" && ghostValue > 0) {
    return Math.round(ghostValue);
  }
  const words = html
    .replace(/<[^>]*>/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
}

/** Collapses whitespace and cuts at a word boundary with an ellipsis. */
export function truncateText(text: string, max: number): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export function mapTag(raw: RawTag): BlogTag {
  return {
    id: raw.id,
    slug: raw.slug,
    name: raw.name,
    description: raw.description ?? null,
    postCount: raw.count?.posts ?? null,
  };
}

function mapAuthor(raw: RawAuthor): BlogAuthor {
  return {
    id: raw.id,
    slug: raw.slug,
    name: raw.name,
    profileImage: raw.profile_image ?? null,
    bio: raw.bio ?? null,
  };
}

/** Internal (`#hash`) tags are CMS plumbing and never shown. */
const isPublicTag = (tag: RawTag) =>
  !tag.visibility || tag.visibility === "public";

/**
 * `lang` overrides the tag-derived language: a list requested with a language
 * filter already knows it, whether or not the API echoes internal tags.
 */
export function mapPostSummary(raw: RawPost, lang?: Locale): BlogPostSummary {
  const allSlugs = (raw.tags ?? []).map((t) => t.slug);
  const tags = (raw.tags ?? []).filter(isPublicTag).map(mapTag);
  const primary =
    raw.primary_tag && isPublicTag(raw.primary_tag)
      ? mapTag(raw.primary_tag)
      : (tags[0] ?? null);
  const author = raw.primary_author ?? raw.authors?.[0] ?? null;
  return {
    id: raw.id,
    slug: raw.slug,
    title: raw.title,
    excerpt: truncateText(raw.custom_excerpt || raw.excerpt || "", 220),
    featureImage: raw.feature_image ?? null,
    featureImageAlt: raw.feature_image_alt ?? null,
    // Caption is HTML; it is only exposed (sanitized) on the full post.
    featureImageCaption: null,
    publishedAt: raw.published_at,
    updatedAt: raw.updated_at,
    readingTime: readingTimeMinutes(raw.reading_time, raw.html ?? ""),
    tags,
    primaryTag: primary,
    lang: lang ?? langFromTagSlugs(allSlugs),
    translationKey: translationKeyFromTagSlugs(allSlugs),
    author: author ? mapAuthor(author) : null,
    metaTitle: raw.meta_title ?? null,
    metaDescription: raw.meta_description ?? null,
    ogImage: raw.og_image ?? null,
  };
}

/** Full post; the HTML is sanitized here, once, at the API boundary. */
export function mapPost(raw: RawPost, lang?: Locale): BlogPost {
  const host = ghostHost();
  return {
    ...mapPostSummary(raw, lang),
    featureImageCaption:
      sanitizePostHtml(raw.feature_image_caption ?? "", { cmsHost: host }) ||
      null,
    html: sanitizePostHtml(raw.html ?? "", { cmsHost: host }),
  };
}

/** Drops the body: list payloads and prerender preloads stay small. */
export function toSummary(post: BlogPost | BlogPostSummary): BlogPostSummary {
  const { html: _html, ...summary } = post as BlogPost;
  void _html;
  return summary;
}

/* ------------------------------------------------------------------ */
/* Requests                                                            */
/* ------------------------------------------------------------------ */

type Params = Record<string, string | number | undefined>;

const REQUEST_TIMEOUT_MS = 10_000;

/** Backoff base for build-time retries (tests shrink it). */
export const retryPolicy = { attempts: 3, baseMs: 500 };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function ghostGetOnce<T>(
  config: GhostConfig,
  path: string,
  params: Params,
  signal?: AbortSignal,
): Promise<T | null> {
  const query = new URLSearchParams({ key: config.key });
  for (const [name, value] of Object.entries(params)) {
    if (value !== undefined) query.set(name, String(value));
  }
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const res = await fetch(
    `${config.url}/ghost/api/content/${path}/?${query.toString()}`,
    {
      // No custom headers: keeps cross-origin browser calls "simple" requests.
      signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
    },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new GhostError(
      `Ghost Content API ${res.status} for ${path}`,
      res.status,
    );
  }
  return (await res.json()) as T;
}

/**
 * `retry` (build time): up to `retryPolicy.attempts` tries with exponential
 * backoff on network errors, 5xx and 429. Browser calls try once; React
 * Query owns retries there.
 */
async function ghostGet<T>(
  config: GhostConfig,
  path: string,
  params: Params = {},
  signal?: AbortSignal,
  retry = false,
): Promise<T | null> {
  const attempts = retry ? retryPolicy.attempts : 1;
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await ghostGetOnce<T>(config, path, params, signal);
    } catch (error) {
      const status = error instanceof GhostError ? error.status : undefined;
      const transient = status === undefined || status >= 500 || status === 429;
      if (attempt >= attempts || !transient || signal?.aborted) throw error;
      await sleep(retryPolicy.baseMs * 2 ** (attempt - 1));
    }
  }
}

const EMPTY_PAGINATION: BlogPagination = {
  page: 1,
  limit: 0,
  pages: 1,
  total: 0,
  next: null,
  prev: null,
};

/** NQL string literal, so a tag slug can never alter the filter. */
function nqlString(value: string): string {
  return `'${value.replace(/[\\']/g, "\\$&")}'`;
}

/**
 * NQL for "posts written in `lang`". Non-English: the `#lang-*` internal tag.
 * English: no `#lang-*` tag of any other language (`tag:-[a,b]` = none of).
 */
export function langFilter(lang: Locale): string {
  if (lang === DEFAULT_LOCALE) {
    return `tag:-[${TAGGED_LOCALES.map(langTagSlug).join(",")}]`;
  }
  return `tag:${langTagSlug(lang)}`;
}

/** Joins NQL clauses with AND (`+`). */
function nqlAnd(...clauses: (string | undefined)[]): string | undefined {
  const parts = clauses.filter(Boolean);
  return parts.length > 0 ? parts.join("+") : undefined;
}

/**
 * Internal Ghost tag `#whats-new`: marks a post as a product news item for the
 * in-app "What's new" card, which reads them straight from the Content API.
 * Such posts are not blog posts, so every blog query excludes them.
 */
export const NEWS_TAG_SLUG = "hash-whats-new";
const NOT_NEWS = `tag:-${NEWS_TAG_SLUG}`;

export type ListPostsOptions = {
  /** Only posts written in this language. */
  lang: Locale;
  limit?: number;
  page?: number;
  tag?: string;
  signal?: AbortSignal;
};

type PostsResponse = {
  posts: RawPost[];
  meta?: { pagination?: BlogPagination };
};

/** Newest first. Empty page (no throw) when the blog is not configured. */
export async function listPosts({
  lang,
  limit = 9,
  page = 1,
  tag,
  signal,
}: ListPostsOptions): Promise<BlogPage> {
  const config = resolveGhostConfig();
  if (!config) return { posts: [], pagination: EMPTY_PAGINATION };
  const data = await ghostGet<PostsResponse>(
    config,
    "posts",
    {
      include: "tags,authors",
      limit,
      page,
      order: "published_at desc",
      filter: nqlAnd(
        NOT_NEWS,
        tag ? `tag:${nqlString(tag)}` : undefined,
        langFilter(lang),
      ),
    },
    signal,
  );
  return {
    posts: (data?.posts ?? []).map((raw) => mapPostSummary(raw, lang)),
    pagination: data?.meta?.pagination ?? EMPTY_PAGINATION,
  };
}

/** One post by slug, or null when it does not exist / is unconfigured. */
export async function getPost(
  slug: string,
  signal?: AbortSignal,
): Promise<BlogPost | null> {
  const config = resolveGhostConfig();
  if (!config || !slug) return null;
  const data = await ghostGet<PostsResponse>(
    config,
    `posts/slug/${encodeURIComponent(slug)}`,
    { include: "tags,authors" },
    signal,
  );
  const raw = data?.posts?.[0];
  return raw ? mapPost(raw) : null;
}

export type BlogPostLink = { slug: string; title: string };

/** Posts of one translation group (`#t-<key>`), one entry per language. */
export async function listTranslations(
  key: string,
  signal?: AbortSignal,
): Promise<TranslationLink[]> {
  const config = resolveGhostConfig();
  if (!config || !isTranslationKey(key)) return [];
  const data = await ghostGet<PostsResponse>(
    config,
    "posts",
    {
      include: "tags",
      limit: 20,
      order: "published_at desc",
      filter: `tag:${translationTagSlug(key)}`,
    },
    signal,
  );
  const seen = new Set<Locale>();
  const out: TranslationLink[] = [];
  for (const raw of data?.posts ?? []) {
    const { lang, slug } = mapPostSummary(raw);
    if (seen.has(lang)) continue;
    seen.add(lang);
    out.push({ lang, slug });
  }
  return out;
}

/** Slug + title of every post in `lang` (optionally one tag), for crawlable index lists. */
export async function listPostLinks({
  lang,
  tag,
  signal,
  retry = false,
}: {
  lang: Locale;
  tag?: string;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<BlogPostLink[]> {
  const config = resolveGhostConfig();
  if (!config) return [];
  const out: BlogPostLink[] = [];
  for (let page = 1; page <= 50; page += 1) {
    const data = await ghostGet<{
      posts: { slug: string; title: string }[];
      meta?: { pagination?: BlogPagination };
    }>(
      config,
      "posts",
      {
        fields: "slug,title",
        limit: 100,
        page,
        order: "published_at desc",
        filter: nqlAnd(
          NOT_NEWS,
          tag ? `tag:${nqlString(tag)}` : undefined,
          langFilter(lang),
        ),
      },
      signal,
      retry,
    );
    out.push(
      ...(data?.posts ?? []).map(({ slug, title }) => ({ slug, title })),
    );
    if (!data?.meta?.pagination?.next) break;
  }
  return out;
}

/** Public tags that have at least one post (Ghost omits empty tags). */
export async function listTags(
  signal?: AbortSignal,
  retry = false,
): Promise<BlogTag[]> {
  const config = resolveGhostConfig();
  if (!config) return [];
  const data = await ghostGet<{ tags: RawTag[] }>(
    config,
    "tags",
    {
      include: "count.posts",
      filter: "visibility:public",
      limit: 100,
      order: "name asc",
    },
    signal,
    retry,
  );
  return (data?.tags ?? []).map(mapTag);
}

/**
 * Public tags of the posts written in `lang`, with their post counts within
 * that language (the tags endpoint counts posts of every language). Pages the
 * posts of the language asking only for ids and their tags. If the API does
 * not echo tags on that request (the docs warn `fields` and `include` do not
 * always combine), falls back to the global tag list rather than showing none.
 */
export async function listLangTags(
  lang: Locale,
  signal?: AbortSignal,
  retry = false,
): Promise<BlogTag[]> {
  const config = resolveGhostConfig();
  if (!config) return [];
  const found = new Map<string, BlogTag>();
  let sawPosts = false;
  let sawTags = false;
  for (let page = 1; page <= 20; page += 1) {
    const data = await ghostGet<{
      posts: Pick<RawPost, "tags">[];
      meta?: { pagination?: BlogPagination };
    }>(
      config,
      "posts",
      {
        include: "tags",
        fields: "id",
        limit: 100,
        page,
        filter: nqlAnd(NOT_NEWS, langFilter(lang)),
      },
      signal,
      retry,
    );
    for (const post of data?.posts ?? []) {
      sawPosts = true;
      if (!Array.isArray(post.tags)) continue;
      sawTags = true;
      for (const raw of post.tags.filter(isPublicTag)) {
        const entry = found.get(raw.slug) ?? { ...mapTag(raw), postCount: 0 };
        entry.postCount = (entry.postCount ?? 0) + 1;
        found.set(raw.slug, entry);
      }
    }
    if (!data?.meta?.pagination?.next) break;
  }
  if (sawPosts && !sawTags) return listTags(signal, retry);
  return [...found.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Posts matching `filter` with full HTML, page by page (build time). */
async function fetchPostsWhere(filter: string): Promise<BlogPost[]> {
  const config = resolveGhostConfig();
  if (!config) return [];
  const all: BlogPost[] = [];
  for (let page = 1; ; page += 1) {
    const data = await ghostGet<PostsResponse>(
      config,
      "posts",
      {
        include: "tags,authors",
        limit: 50,
        page,
        order: "published_at desc",
        filter,
      },
      undefined,
      true,
    );
    all.push(...(data?.posts ?? []).map((raw) => mapPost(raw)));
    const next = data?.meta?.pagination?.next;
    if (!next || page > 200) break;
  }
  return all;
}

/** Every blog post with full HTML (build time: prerender + RSS). News excluded. */
export function fetchAllPosts(): Promise<BlogPost[]> {
  return fetchPostsWhere(NOT_NEWS);
}

/**
 * Cheap change detector for the runtime renderer (blog-runtime.mjs): the post
 * count plus the newest `updated_at` of the blog posts, one tiny request. An
 * edit bumps `updated_at` and a publish/unpublish/delete moves the count, so
 * the signature changes whenever the blog does. `null` when unconfigured.
 */
export async function fetchBlogSignature(): Promise<string | null> {
  const config = resolveGhostConfig();
  if (!config) return null;
  const data = await ghostGet<{
    posts?: { updated_at?: string }[];
    meta?: { pagination?: { total?: number } };
  }>(config, "posts", {
    limit: 1,
    fields: "updated_at",
    order: "updated_at desc",
    filter: NOT_NEWS,
  });
  return `${data?.meta?.pagination?.total ?? 0}|${data?.posts?.[0]?.updated_at ?? "none"}`;
}

export type BlogSnapshot = {
  configured: boolean;
  posts: BlogPost[];
  tags: BlogTag[];
};

/** `BLOG_ALLOW_EMPTY=1` lets a configured-but-unreachable CMS build empty. */
export function blogAllowEmpty(): boolean {
  // Build-time switch only; not a turbo task input.
  const env = typeof process !== "undefined" ? process.env : {};
  return env.BLOG_ALLOW_EMPTY === "1";
}

/**
 * Build-time snapshot. Unconfigured (no URL or key): warns and yields an
 * empty snapshot, so the build succeeds with "Posts coming soon". Configured
 * but failing after retries: throws, failing the build, so a CMS outage can
 * never deploy a site that silently lost its blog. `BLOG_ALLOW_EMPTY=1`
 * downgrades that to a warning.
 */
export async function loadBlogSnapshot(): Promise<BlogSnapshot> {
  const config = resolveGhostConfig();
  if (!config) {
    console.warn(
      "[blog] VITE_GHOST_URL / GHOST_CONTENT_API_KEY not set: building without blog posts.",
    );
    return { configured: false, posts: [], tags: [] };
  }
  try {
    const [posts, tags] = await Promise.all([
      fetchAllPosts(),
      listTags(undefined, true),
    ]);
    console.log(
      `[blog] fetched ${posts.length} posts, ${tags.length} tags from ${config.url}`,
    );
    return { configured: true, posts, tags };
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    if (blogAllowEmpty()) {
      console.warn(
        `[blog] Ghost at ${config.url} unreachable (${reason}): BLOG_ALLOW_EMPTY=1, building without blog posts.`,
      );
      return { configured: true, posts: [], tags: [] };
    }
    throw new GhostError(
      `Ghost at ${config.url} is configured but unreachable (${reason}). Failing the build; set BLOG_ALLOW_EMPTY=1 to build without posts.`,
    );
  }
}

/** Related posts: same primary tag first, then newest, never the post itself. */
export function pickRelated(
  current: BlogPostSummary,
  candidates: BlogPostSummary[],
  count = 3,
): BlogPostSummary[] {
  const seen = new Set<string>([current.slug]);
  const others = candidates.filter((p) => {
    if (seen.has(p.slug)) return false;
    seen.add(p.slug);
    return true;
  });
  const tagSlug = current.primaryTag?.slug;
  const sameTag = tagSlug
    ? others.filter((p) => p.tags.some((t) => t.slug === tagSlug))
    : [];
  const rest = others.filter((p) => !sameTag.includes(p));
  return [...sameTag, ...rest].slice(0, count).map(toSummary);
}
